import { Component, OnDestroy, OnInit } from '@angular/core';
import { LegacyApiService } from '@lmc-app/core/services/legacy-api.service';
import { CustomFunctionService } from '../../../../../custom-function.service';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Subject, takeUntil } from 'rxjs';
import { Sort } from '@angular/material/sort';
import { selectUser, State } from '@lmc-app/store';
import { User } from '@lmc-app/shared/interface/user.interface';

export interface Head {
	groupName: string;
	badDebt: number;
	fundedAmount: number;
	countDeliquency: number;
	countFunded: number;
	delinquencyRate: number;
	defaultAmount: number;
	countDefault: number;
	defaultRate: number;
}

@Component({
	selector: 'app-nlscreditorcollections',
	templateUrl: './nlscreditorcollections.component.html',
	styleUrls: ['./nlscreditorcollections.component.scss'],
})
export class NlscreditorcollectionsComponent implements OnInit, OnDestroy {
	private readonly _onDestroy = new Subject<void>();

	agencyName: string;
	groupCreditorArray: Head[] = [];
	sortedData: Head[];
	countGroupBadDebt: number;
	amountGroupBadDebt: any;
	countGroupFunded: number;
	amountGroupFunded: any;
	countGroupDefault: number;
	amountGroupDefault: any;

	constructor(
		private apiService: LegacyApiService,
		private customFunction: CustomFunctionService,
		private router: ActivatedRoute,
		private route: Router,
		private store: Store<State>
	) {}

	ngOnInit(): void {
		this.store
			.select(selectUser)
			.pipe(takeUntil(this._onDestroy))
			.subscribe((user: User | null) => {
				if (user) {
					// Get the creditor name the nlsagencygrouped page link
					this.router.queryParams.subscribe((params) => {
						this.agencyName = params['agency'];
					});

					// Send the agency name to get the agencies listed under the creditor
					this.apiService
						.getNLSagencyCollectionsCollectors(this.agencyName)
						.subscribe((res) => {
								console.log(res.data);
								if (res.message === 'Success') {
									const filterCreditor = this.customFunction.filterDuplicates(
										res.data,
										'Creditor'
									);

									// Loop through the filtered provinces
									for (let i = 0; i < filterCreditor.length; i++) {
										let countDeliquency = 0;
										let countDefault = 0;
										let fieldName = '';
										let badDebt = 0;
										let defaultAmount = 0;
										let fundedAmount = 0;
										let countFunded = 0;
										let delinquencyRate = '';
										let defaultRate = '';

										// Loop through the overall raw data
										for (let j = 0; j < res.data.length; j++) {
											// If filtered creditor match the raw data agencies
											if (filterCreditor[i] === res.data[j].Creditor) {
												fieldName = res.data[j].Creditor; // Get the collector name
												fundedAmount += res.data[j].original_note_amount; // Add the orignal funded amount to the respective collector
												countFunded++; // count the funded occurrences
												// Delinquency
												if (res.data[j].internal_DPD > 0) {
													badDebt += res.data[j].current_principal_balance; // Add the principal balance to the respective collector
													countDeliquency++; // count the delinquency occurrences
												}
												// Default
												if (res.data[j].internal_DPD > 120) {
													defaultAmount +=
														res.data[j].current_principal_balance; // Add the principal balance to the respective collector
													countDefault++; // count the default occurrences
												}
											}
										}

										delinquencyRate = (
											(badDebt / fundedAmount) *
											100
										).toFixed();
										defaultRate = (
											(defaultAmount / fundedAmount) *
											100
										).toFixed();

										this.groupCreditorArray.push(
											new this.customFunction.groupCollections(
												fieldName,
												badDebt,
												fundedAmount,
												countDeliquency,
												countFunded,
												parseInt(delinquencyRate),
												defaultAmount,
												countDefault,
												parseInt(defaultRate)
											)
										); // Push the loop objects to the new array list
										this.countGroupBadDebt = this.customFunction.sumNumbers(
											this.groupCreditorArray,
											'countDeliquency'
										); // Total up the numbers
										this.amountGroupBadDebt = this.customFunction.sumNumbers(
											this.groupCreditorArray,
											'badDebt'
										);
										this.countGroupFunded = this.customFunction.sumNumbers(
											this.groupCreditorArray,
											'countFunded'
										);
										this.amountGroupFunded = this.customFunction.sumNumbers(
											this.groupCreditorArray,
											'fundedAmount'
										);
										this.countGroupDefault = this.customFunction.sumNumbers(
											this.groupCreditorArray,
											'countDefault'
										);
										this.amountGroupDefault = this.customFunction.sumNumbers(
											this.groupCreditorArray,
											'defaultAmount'
										);
									}
									this.sortData({ active: 'defaultRate', direction: 'desc' });
								} else {
									this.groupCreditorArray = [];
									this.countGroupBadDebt = 0;
									this.amountGroupBadDebt = 0;
									this.countGroupFunded = 0;
									this.amountGroupFunded = 0;
									this.countGroupDefault = 0;
									this.amountGroupDefault = 0;
								}
							});
				}
			});
	}

	ngOnDestroy(): void {
		this._onDestroy.next();
		this._onDestroy.complete();
	}

	sortData(sort: Sort) {
		const data = this.groupCreditorArray.slice();
		if (!sort.active || sort.direction === '') {
			this.sortedData = data;
			return;
		}

		this.sortedData = data.sort((a, b) => {
			const isAsc = sort.direction === 'asc';
			switch (sort.active) {
				case 'groupName':
					return this.customFunction.compare(a.groupName, b.groupName, isAsc);
				case 'badDebt':
					return this.customFunction.compare(a.badDebt, b.badDebt, isAsc);
				case 'fundedAmount':
					return this.customFunction.compare(
						a.fundedAmount,
						b.fundedAmount,
						isAsc
					);
				case 'countDeliquency':
					return this.customFunction.compare(
						a.countDeliquency,
						b.countDeliquency,
						isAsc
					);
				case 'countFunded':
					return this.customFunction.compare(
						a.countFunded,
						b.countFunded,
						isAsc
					);
				case 'delinquencyRate':
					return this.customFunction.compare(
						a.delinquencyRate,
						b.delinquencyRate,
						isAsc
					);
				case 'defaultAmount':
					return this.customFunction.compare(
						a.defaultAmount,
						b.defaultAmount,
						isAsc
					);
				case 'countDefault':
					return this.customFunction.compare(
						a.countDefault,
						b.countDefault,
						isAsc
					);
				case 'defaultRate':
					return this.customFunction.compare(
						a.defaultRate,
						b.defaultRate,
						isAsc
					);
				default:
					return 0;
			}
		});
	}
}
